import { useTranslation } from "react-i18next";

import type { HttpMethod } from "@/auth/permissions/permission-targets";
import { PERMISSION_TARGETS } from "@/auth/permissions/permission-targets";
import { useCanFn } from "@/auth/permissions/use-permissions";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

import { useMyGroups } from "./use-my-access";

/**
 * 画面に出す権限の一覧。
 *
 * 翻訳キーはテンプレート文字列で組み立てず個別に書く（i18n の型定義が
 * 効くようにするため）。`key` は testid 用。
 */
const PERMISSION_ROWS: readonly {
  readonly key: string;
  readonly labelKey:
    | "permissions.targets.caseListMine"
    | "permissions.targets.caseListAll"
    | "permissions.targets.taskList"
    | "permissions.targets.processModelRead"
    | "permissions.targets.processModelWrite"
    | "permissions.targets.secretList"
    | "permissions.targets.userSearch";
  readonly target: string;
  readonly method: HttpMethod;
}[] = [
  {
    key: "caseListMine",
    labelKey: "permissions.targets.caseListMine",
    target: PERMISSION_TARGETS.processInstanceListForMe,
    method: "POST",
  },
  {
    key: "caseListAll",
    labelKey: "permissions.targets.caseListAll",
    target: PERMISSION_TARGETS.processInstanceList,
    method: "POST",
  },
  {
    key: "taskList",
    labelKey: "permissions.targets.taskList",
    target: PERMISSION_TARGETS.taskList,
    method: "GET",
  },
  {
    key: "processModelRead",
    labelKey: "permissions.targets.processModelRead",
    target: PERMISSION_TARGETS.processModelList,
    method: "GET",
  },
  {
    key: "processModelWrite",
    labelKey: "permissions.targets.processModelWrite",
    target: PERMISSION_TARGETS.processModelList,
    method: "POST",
  },
  {
    key: "secretList",
    labelKey: "permissions.targets.secretList",
    target: PERMISSION_TARGETS.secretList,
    method: "GET",
  },
  {
    key: "userSearch",
    labelKey: "permissions.targets.userSearch",
    target: PERMISSION_TARGETS.userSearch,
    method: "GET",
  },
];

/**
 * 大項目6「所属と権限」（旧称「権限・グループ管理」）。
 *
 * SpiffArena にグループ一覧 API が無く、所属の正本は Zeus IdP の claim なので
 * （#24）、この画面は「自分の所属と権限の**確認**」に限る。編集は IdP / zeus.yml 側。
 *
 * 管理機能ではないので**全員に見せる**（「なぜ承認できないのか」を自分で確かめられる）。
 * ナビも管理セクションではなく業務セクションに置く。
 */
export function PermissionsPage() {
  const { t } = useTranslation("common");
  const groups = useMyGroups();
  const can = useCanFn();

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{t("permissions.title")}</h1>
        <p className="text-muted-foreground text-sm">
          {t("permissions.description")}
        </p>
      </header>

      <p className="bg-muted text-muted-foreground rounded-md px-3 py-2 text-xs">
        {t("permissions.sotNote")}
      </p>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">{t("permissions.myGroups")}</h2>
        {groups.isPending ? (
          <Skeleton className="h-8 w-48" />
        ) : groups.data?.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("permissions.noGroups")}
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {groups.data?.map((group) => (
              <li key={group}>
                <Badge variant="secondary">{group}</Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">
          {t("permissions.myPermissions")}
        </h2>
        <ul className="flex flex-col gap-1 text-sm">
          {PERMISSION_ROWS.map((row) => {
            const allowed = can(row.target, row.method);
            return (
              <li
                key={`${row.key}:${row.method}`}
                data-testid={`perm-${row.key}`}
                className="border-border flex items-center justify-between gap-3 border-b pb-1 last:border-0"
              >
                <span>{t(row.labelKey)}</span>
                <Badge variant={allowed ? "secondary" : "outline"}>
                  {allowed ? t("permissions.allowed") : t("permissions.denied")}
                </Badge>
              </li>
            );
          })}
        </ul>
      </section>
    </section>
  );
}
